import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import { BOOK_SHOWN } from '../../lib/bookAsk'
import { bookAskOf, bookFilterFrom, bookSearchFrom, emptyBookSays, searchFindsInactive } from '../../lib/bookFilter'
import { withActiveFirst } from '../../lib/inactive'
import { apiErrorMessage } from '../../lib/errors'
import { formatPercentage } from '../../lib/format'
import { useBookFilter } from '../../lib/useBookFilter'
import type { TableColumn } from '../../lib/tableView'
import { useAddressTableView } from '../../lib/useAddressTableView'
import { ActivationBadge } from '../../components/ActivationPanel'
import { BookFilterBar } from '../../components/BookFilterBar'
import { FilterCell, FilterSummary, SortableHeader } from '../../components/TableHeads'
import { TableScroll } from '../../components/TableScroll'
import { historyRow } from '../../components/historyRow'
import { ComplianceBadge } from '../brokers/ComplianceBadge'
import { complianceRank } from '../brokers/compliance'
import { formatMcNumber } from '../brokers/format'
import { listCarriers, type CarrierSummary } from './api'

/**
 * What the book's columns sort and filter by (TRKC-110). The name, the MC number, the paperwork,
 * the working relationship and the state are filtered from the bar above — the server's filters,
 * which reach past the first 500 listed — so their headings sort and their boxes stay empty;
 * the commission and the city are filtered here.
 */
/**
 * Inactive carriers — listed when the filter asks for them, or when a search finds them — stay
 * after the active ones whatever the column, as history does in every table (TRKC-110).
 */
const activeFirst = (carrier: CarrierSummary) => (carrier.active ? 0 : 1)

const CARRIER_COLUMNS: readonly TableColumn<CarrierSummary>[] = [
  { key: 'name', sortValue: (carrier) => carrier.name },
  { key: 'mc', sortValue: (carrier) => carrier.mcNumber },
  {
    key: 'commission',
    sortValue: (carrier) => carrier.commissionPercentage,
    filter: { kind: 'atLeast', value: (carrier) => carrier.commissionPercentage, keepUnknown: false },
  },
  {
    key: 'location',
    sortValue: (carrier) => `${carrier.city}, ${carrier.state}`,
    filter: { kind: 'text', text: (carrier) => `${carrier.city}, ${carrier.state}` },
  },
  { key: 'compliance', sortValue: (carrier) => complianceRank(carrier.complianceState) },
]

/**
 * The carrier book: everyone the operation dispatches for, searchable by name or MC and
 * narrowed the way the broker book is (TRKC-97) — the same bar, kept in the address.
 */
export function CarriersPage() {
  const { t, i18n } = useTranslation()
  const controls = useBookFilter()
  const { filter } = controls

  const [carriers, setCarriers] = useState<CarrierSummary[] | null>(null)
  // Stored unresolved and translated at render, so a language switch re-translates it.
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const asked = bookSearchFrom(filter).toString()
  const listed = useMemo(() => withActiveFirst((carriers ?? []).slice(0, BOOK_SHOWN)), [carriers])
  const view = useAddressTableView(listed, CARRIER_COLUMNS, activeFirst)

  // Keyed on what the filter says, never on `t`, and only the latest request writes —
  // the broker book's rules, each kept here on its own.
  useEffect(() => {
    let active = true
    setIsLoading(true)
    setLoadFailure(null)

    // One past what the page lists, so it can say when the search should narrow the book.
    listCarriers({ ...bookAskOf(bookFilterFrom(new URLSearchParams(asked))), limit: BOOK_SHOWN + 1 })
      .then((found) => {
        if (active) {
          setCarriers(found)
        }
      })
      .catch((caught: unknown) => {
        if (active) {
          setCarriers(null)
          setLoadFailure({ error: caught })
        }
      })
      .finally(() => {
        if (active) {
          setIsLoading(false)
        }
      })

    return () => {
      active = false
    }
  }, [asked])

  return (
    <section className="page">
      <header className="page__header page__header--row">
        <div>
          <h1>{t('carriers.title')}</h1>
          <p className="page__subtitle">{t('carriers.subtitle')}</p>
        </div>
        <Link className="button" to="/carriers/new">
          {t('carriers.register')}
        </Link>
      </header>

      <BookFilterBar book="carriers" controls={controls} isLoading={isLoading} />

      {loadFailure !== null && (
        <p className="alert" role="alert">
          {apiErrorMessage(loadFailure.error, t, 'carriers.loadError')}
        </p>
      )}

      {carriers !== null && carriers.length === 0 && (
        <p className="placeholder">
          {t(`carriers.${emptyBookSays(filter)}`)}
        </p>
      )}

      {/* A search at the default relationship finds the inactive ones too, and says so. */}
      {carriers !== null && searchFindsInactive(filter) && carriers.some((carrier) => !carrier.active) && (
        <p className="muted">{t('books.searchFindsInactive')}</p>
      )}

      {carriers !== null && carriers.length > BOOK_SHOWN && (
        <p className="muted" role="status">
          {t('carriers.firstOnly', { shown: BOOK_SHOWN })}
        </p>
      )}

      {carriers !== null && carriers.length > 0 && (
        <TableScroll label={t('carriers.title')}>
          <table className="table">
            <caption className="visually-hidden">{t('carriers.title')}</caption>
            <thead>
              <tr>
                <SortableHeader view={view} column="name">{t('carriers.name')}</SortableHeader>
                <SortableHeader view={view} column="mc">{t('carriers.mcNumber')}</SortableHeader>
                <SortableHeader view={view} column="commission">{t('carriers.commission')}</SortableHeader>
                <SortableHeader view={view} column="location">{t('carriers.location')}</SortableHeader>
                <SortableHeader view={view} column="compliance">{t('carriers.compliance')}</SortableHeader>
              </tr>
              <tr className="table__filters">
                {CARRIER_COLUMNS.map((column) => (
                  <FilterCell key={column.key} view={view} column={column} label={t(CARRIER_HEADINGS[column.key] ?? column.key)} />
                ))}
              </tr>
            </thead>
            <tbody>
              {view.rows.length === 0 && (
                <tr>
                  <td colSpan={CARRIER_COLUMNS.length} className="placeholder">
                    {t('carriers.noMatches')}
                  </td>
                </tr>
              )}
              {view.rows.map((carrier) => (
                <tr key={carrier.id} className={historyRow(!carrier.active)}>
                  <td>
                    <Link to={`/carriers/${carrier.id}`}>{carrier.name}</Link>
                  </td>
                  <td className="table__numeric">{formatMcNumber(carrier.mcNumber) ?? '—'}</td>
                  <td className="table__numeric">
                    {formatPercentage(carrier.commissionPercentage, i18n.language)}
                  </td>
                  <td>{`${carrier.city}, ${carrier.state}`}</td>
                  <td>
                    <ComplianceBadge state={carrier.complianceState} />{!carrier.active && ' '}
                    <ActivationBadge active={carrier.active} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      {carriers !== null && carriers.length > 0 && <FilterSummary view={view} />}
    </section>
  )
}

/** The heading each column's filter box is named after. */
const CARRIER_HEADINGS: Record<string, string> = {
  name: 'carriers.name',
  mc: 'carriers.mcNumber',
  commission: 'carriers.commission',
  location: 'carriers.location',
  compliance: 'carriers.compliance',
}
